import { storeToRefs } from 'pinia';
import { type InjectionKey, type Ref } from 'vue';

import { useAuthStore } from '@/stores/auth';
import type { CurrentUser } from '@/shared/types/auth';

import {
  type ActionScopesArray, isGranted as isGrantedBase,
} from '@pcg/auth';
import type { Permission } from '@dott/permissions';
import { useLogout } from './useLogout';

export const CURRENT_USER_KEY = Symbol('CURRENT_USER_KEY') as InjectionKey<Ref<CurrentUser>>;

export const useAuth = () => {
  const store = useAuthStore();
  const { user } = storeToRefs(store);

  if (!user.value) {
    throw new Error('User must be fetched before using it.');
  }

  // Scope is guaranteed to be present by scopeMiddleware
  // Pages using useAuth are only accessible when user has a valid scope
  const currentUser = user as Ref<CurrentUser>;

  const isGranted = (permission: Permission, actionScopes?: ActionScopesArray) => {
    if (!currentUser.value) {
      return false;
    }

    return isGrantedBase(currentUser.value, permission, actionScopes);
  };

  return {
    currentUser,
    isGranted,
    ...useLogout(),
  };
};
